import { signUp, signIn, signOut, getCurrentUser, confirmSignUp, resendSignUpCode, fetchAuthSession, resetPassword, confirmResetPassword, } from 'aws-amplify/auth';
import { checkForCrossDomainAuth } from './crossDomainAuth';
class AuthService {
    constructor() {
        this.TOKEN_KEY = 'bri_auth_token';
        this.REFRESH_TOKEN_KEY = 'bri_auth_refresh_token';
        this.IMPERSONATION_MODE_KEY = 'bri_impersonation_mode';
        this.API_BASE_URL = 'https://jz6jdvym87.execute-api.us-east-1.amazonaws.com/dev';
        this.crossDomainAuthChecked = false;
        this.currentUserPromise = null;
        this.profileCache = null;
        this.PROFILE_CACHE_TTL_MS = 5 * 60 * 1000;
    }
    // Allow apps to configure the API URL
    setApiUrl(url) {
        this.API_BASE_URL = url;
    }
    getStoredTokenRecord() {
        if (typeof window === 'undefined')
            return null;
        const token = localStorage.getItem(this.TOKEN_KEY);
        if (!token)
            return null;
        const payload = this.getTokenPayload(token);
        if (!payload) {
            this.clearLocalAuthState();
            return null;
        }
        const expirationTime = typeof payload.exp === 'number' ? payload.exp * 1000 : 0;
        const isExpired = expirationTime > 0 ? Date.now() >= expirationTime : true;
        return {
            token,
            payload,
            isExpired,
            isImpersonation: payload.tokenType === 'impersonation',
            isWorkspaceSwitch: payload.tokenType === 'workspace-switch',
        };
    }
    storeSessionTokens(session) {
        const accessToken = session.tokens?.accessToken?.toString();
        const idToken = session.tokens?.idToken?.toString();
        const token = idToken || accessToken;
        if (!token) {
            throw new Error('No authentication tokens received from Cognito');
        }
        localStorage.setItem(this.TOKEN_KEY, token);
        this.profileCache = null;
        this.setImpersonationMode(false);
        return token;
    }
    async recoverStandardSessionToken(forceRefresh) {
        try {
            const session = await fetchAuthSession({ forceRefresh });
            if (!session.tokens) {
                return null;
            }
            return this.storeSessionTokens(session);
        }
        catch (error) {
            console.warn(`authService: Failed to recover Cognito session (forceRefresh=${forceRefresh})`, error);
            return null;
        }
    }
    async getValidToken() {
        const storedToken = this.getStoredTokenRecord();
        if (storedToken && !storedToken.isExpired) {
            return storedToken.token;
        }
        const impersonationModeActive = this.isImpersonationModeActive() || !!storedToken?.isImpersonation;
        if (impersonationModeActive) {
            this.clearLocalAuthState();
            throw new Error('Impersonation session expired. Please sign in again.');
        }
        const passiveRecoveryToken = await this.recoverStandardSessionToken(false);
        if (passiveRecoveryToken) {
            void this.sendFrontendTelemetry('frontend.auth_token_recovered', 'shared-auth', {
                recoveryMode: 'passive',
            });
            return passiveRecoveryToken;
        }
        const recoveredToken = await this.recoverStandardSessionToken(true);
        if (recoveredToken) {
            void this.sendFrontendTelemetry('frontend.auth_token_recovered', 'shared-auth', {
                recoveryMode: 'force_refresh',
            });
            return recoveredToken;
        }
        console.warn('authService: Could not recover an authenticated session');
        this.clearLocalAuthState();
        throw new Error('Authentication session expired. Please sign in again.');
    }
    setImpersonationMode(isActive) {
        if (isActive) {
            localStorage.setItem(this.IMPERSONATION_MODE_KEY, 'true');
            localStorage.removeItem(this.REFRESH_TOKEN_KEY);
            return;
        }
        localStorage.removeItem(this.IMPERSONATION_MODE_KEY);
    }
    clearLocalAuthState() {
        localStorage.removeItem(this.TOKEN_KEY);
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
        localStorage.removeItem(this.IMPERSONATION_MODE_KEY);
        this.profileCache = null;
    }
    isImpersonationModeActive() {
        return localStorage.getItem(this.IMPERSONATION_MODE_KEY) === 'true';
    }
    getErrorName(error) {
        if (typeof error === 'object' && error !== null && 'name' in error && typeof error.name === 'string') {
            return error.name;
        }
        return undefined;
    }
    getErrorMessage(error) {
        if (error instanceof Error) {
            return error.message;
        }
        if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
            return error.message;
        }
        if (typeof error === 'string') {
            return error;
        }
        return undefined;
    }
    /**
     * Check for cross-domain authentication on initialization
     * This should be called before checking for existing sessions
     */
    checkCrossDomainAuth() {
        if (!this.crossDomainAuthChecked) {
            this.crossDomainAuthChecked = true;
            const wasAuthenticated = checkForCrossDomainAuth();
            return wasAuthenticated;
        }
        return false;
    }
    /**
     * Sign in with email and password using AWS Cognito
     */
    async signIn(email, password) {
        try {
            const { isSignedIn, nextStep } = await signIn({
                username: email,
                password,
            });
            if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
                throw new Error('Please verify your email address before signing in');
            }
            if (!isSignedIn) {
                throw new Error('Sign in failed');
            }
            // Get the Cognito tokens and store them
            const session = await fetchAuthSession();
            const accessToken = session.tokens?.accessToken?.toString();
            const idToken = session.tokens?.idToken?.toString();
            // Prioritize ID token as it contains user information, then access token
            if (idToken) {
                localStorage.setItem(this.TOKEN_KEY, idToken);
                this.setImpersonationMode(false);
            }
            else if (accessToken) {
                localStorage.setItem(this.TOKEN_KEY, accessToken);
                this.setImpersonationMode(false);
            }
            else {
                console.warn('No Cognito tokens available, this should not happen after successful sign-in');
                throw new Error('No authentication tokens received from Cognito');
            }
            // Get user profile from API
            const userProfile = await this.fetchUserProfile(email);
            return userProfile;
        }
        catch (error) {
            console.error('Cognito signin error:', error);
            const errorName = this.getErrorName(error);
            const errorMessage = this.getErrorMessage(error);
            // Handle specific Cognito errors
            if (errorName === 'UserNotConfirmedException') {
                throw new Error('Please verify your email address before signing in');
            }
            else if (errorName === 'NotAuthorizedException') {
                throw new Error('Invalid email or password');
            }
            else if (errorName === 'UserNotFoundException') {
                throw new Error('No account found with this email address');
            }
            else if (errorName === 'UserAlreadyAuthenticatedException') {
                // User is already signed in, just return their profile
                try {
                    const session = await fetchAuthSession();
                    if (session.tokens) {
                        const accessToken = session.tokens.accessToken?.toString();
                        const idToken = session.tokens.idToken?.toString();
                        // Prioritize ID token as it contains user profile information
                        if (idToken) {
                            localStorage.setItem(this.TOKEN_KEY, idToken);
                            this.setImpersonationMode(false);
                        }
                        else if (accessToken) {
                            localStorage.setItem(this.TOKEN_KEY, accessToken);
                            this.setImpersonationMode(false);
                        }
                        return await this.fetchUserProfile(email);
                    }
                }
                catch (sessionError) {
                    console.error('Failed to get session for already authenticated user:', sessionError);
                }
                // No fallback - require proper authentication
                throw new Error('Authentication failed - please sign in again');
            }
            else if (errorMessage) {
                throw new Error(errorMessage);
            }
            else {
                throw new Error('Authentication failed');
            }
        }
    }
    /**
     * Sign up new user with AWS Cognito
     */
    async signUp(name, email, password) {
        try {
            await signUp({
                username: email,
                password,
                options: {
                    userAttributes: {
                        email,
                        name,
                    },
                },
            });
        }
        catch (error) {
            console.error('Cognito signup error:', error);
            const errorName = this.getErrorName(error);
            const errorMessage = this.getErrorMessage(error);
            // Handle specific Cognito errors
            if (errorName === 'UsernameExistsException') {
                throw new Error('An account with this email already exists');
            }
            else if (errorName === 'InvalidPasswordException') {
                throw new Error('Password does not meet requirements');
            }
            else if (errorName === 'InvalidParameterException') {
                throw new Error('Invalid email address or password format');
            }
            else if (errorName === 'UserLambdaValidationException') {
                throw new Error('We could not create your account with this email address. Please use an approved work email or contact support for access.');
            }
            else if (errorMessage) {
                throw new Error(errorMessage);
            }
            else {
                throw new Error('Registration failed. Please try again.');
            }
        }
    }
    /**
     * Confirm sign up with verification code
     */
    async confirmSignUp(email, confirmationCode) {
        try {
            await confirmSignUp({
                username: email,
                confirmationCode,
            });
        }
        catch (error) {
            console.error('Email verification error:', error);
            const errorName = this.getErrorName(error);
            const errorMessage = this.getErrorMessage(error);
            // Handle specific Cognito errors
            if (errorName === 'CodeMismatchException') {
                throw new Error('Invalid verification code. Please check the code and try again.');
            }
            else if (errorName === 'ExpiredCodeException') {
                throw new Error('Verification code has expired. Please request a new code.');
            }
            else if (errorName === 'UserNotFoundException') {
                throw new Error('User not found. Please try signing up again.');
            }
            else if (errorName === 'NotAuthorizedException') {
                throw new Error('Invalid verification code or user already confirmed.');
            }
            else if (errorName === 'AliasExistsException') {
                throw new Error('This email is already associated with another account.');
            }
            else if (errorMessage && errorMessage.includes('PostConfirmation failed')) {
                // Handle PostConfirmation Lambda errors - user is actually confirmed but Lambda failed
                console.warn('PostConfirmation Lambda failed, but user should be confirmed:', errorMessage);
                // Don't throw error here, let the user proceed as they are likely confirmed
            }
            else if (errorMessage) {
                throw new Error(errorMessage);
            }
            else {
                throw new Error('Email verification failed. Please try again.');
            }
        }
    }
    /**
     * Resend verification code
     */
    async resendVerificationCode(email) {
        try {
            await resendSignUpCode({ username: email });
        }
        catch (error) {
            console.error('Resend verification error:', error);
            throw new Error('Failed to resend verification code');
        }
    }
    /**
     * Sign out user from AWS Cognito
     */
    async logout() {
        const token = this.getStoredTokenRecord()?.token || this.getToken();
        const isImpersonating = !!token && (this.isImpersonationModeActive() || this.isImpersonationToken(token));
        try {
            if (isImpersonating && token) {
                try {
                    await fetch(`${this.API_BASE_URL}/user/impersonation/stop`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                    });
                }
                catch (stopError) {
                    console.warn('Logout could not stop impersonation session cleanly', stopError);
                }
            }
            await signOut();
        }
        catch (error) {
            console.error('Logout error:', error);
        }
        finally {
            this.clearLocalAuthState();
        }
    }
    getTokenPayload(token) {
        try {
            const parts = token.split('.');
            if (parts.length !== 3)
                return null;
            return JSON.parse(atob(parts[1]));
        }
        catch (error) {
            console.error('authService: Could not parse token payload:', error);
            return null;
        }
    }
    isImpersonationToken(token) {
        const payload = this.getTokenPayload(token);
        return payload?.tokenType === 'impersonation';
    }
    shouldPreferStoredToken(storedToken) {
        return !!storedToken && !storedToken.isExpired && (storedToken.isImpersonation || storedToken.isWorkspaceSwitch);
    }
    hasActiveImpersonationToken() {
        const token = localStorage.getItem(this.TOKEN_KEY);
        return !!token && this.isImpersonationToken(token);
    }
    async stopImpersonation() {
        const token = this.getToken();
        if (!token) {
            throw new Error('No authentication token');
        }
        const response = await fetch(`${this.API_BASE_URL}/user/impersonation/stop`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        if (!response.ok) {
            throw new Error('Failed to stop impersonation');
        }
        this.clearLocalAuthState();
    }
    async sendFrontendTelemetry(eventType, appName, metadata = {}) {
        try {
            const token = this.getToken();
            if (!token) {
                return;
            }
            await fetch(`${this.API_BASE_URL}/telemetry/frontend`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    eventType,
                    appName,
                    metadata,
                }),
            });
        }
        catch (error) {
            console.warn('Frontend telemetry failed', error);
        }
    }
    /**
     * Get current authenticated user from AWS Cognito
     */
    async getCurrentUser() {
        if (this.currentUserPromise) {
            return this.currentUserPromise;
        }
        this.currentUserPromise = this.loadCurrentUser();
        try {
            return await this.currentUserPromise;
        }
        finally {
            this.currentUserPromise = null;
        }
    }
    getWorkspaceIdFromPayload(payload) {
        const workspaceId = payload?.effectiveWorkspaceId ||
            payload?.['custom:activeWorkspaceId'] ||
            payload?.['custom:workspaceId'] ||
            payload?.workspaceId;
        return typeof workspaceId === 'string' && workspaceId.length > 0 ? workspaceId : null;
    }
    async loadCurrentUser() {
        try {
            // Check for cross-domain authentication first (e.g., navigating from dashboard to qual-analyzer)
            const crossDomainAuthenticated = this.checkCrossDomainAuth();
            // In standalone token modes, behave like a standalone session and avoid
            // rehydrating from the ambient Cognito session underneath it.
            const preferredStoredToken = this.getStoredTokenRecord();
            if (preferredStoredToken?.token &&
                (crossDomainAuthenticated ||
                    this.isImpersonationModeActive() ||
                    this.shouldPreferStoredToken(preferredStoredToken))) {
                this.setImpersonationMode(preferredStoredToken.isImpersonation);
                try {
                    const payload = preferredStoredToken.payload;
                    if (payload?.exp && !preferredStoredToken.isExpired) {
                        const email = payload.email ||
                            payload['cognito:username'] ||
                            payload.username ||
                            'authenticated@user.com';
                        const userProfile = await this.fetchCachedUserProfile(preferredStoredToken.token, email);
                        return userProfile;
                    }
                    this.clearLocalAuthState();
                }
                catch (preferredTokenError) {
                    console.error('authService: Failed to use stored standalone session token:', preferredTokenError);
                    this.clearLocalAuthState();
                }
                return null;
            }
            // First, try to get the session to check if user is authenticated
            try {
                const session = await fetchAuthSession({ forceRefresh: false });
                if (session.tokens && !this.isSessionExpired(session)) {
                    // User has valid tokens, store them
                    const accessToken = session.tokens.accessToken?.toString();
                    const idToken = session.tokens.idToken?.toString();
                    // Prioritize ID token as it contains user profile information
                    if (idToken) {
                        localStorage.setItem(this.TOKEN_KEY, idToken);
                        this.setImpersonationMode(false);
                    }
                    else if (accessToken) {
                        localStorage.setItem(this.TOKEN_KEY, accessToken);
                        this.setImpersonationMode(false);
                    }
                    const profileToken = idToken || accessToken;
                    // Try to get user details and attributes
                    try {
                        await getCurrentUser();
                        // Try to get user attributes which should contain the email
                        let actualEmail = 'authenticated@user.com';
                        try {
                            // Import fetchUserAttributes to get user's email
                            const { fetchUserAttributes } = await import('aws-amplify/auth');
                            const attributes = await fetchUserAttributes();
                            actualEmail = attributes.email || actualEmail;
                        }
                        catch {
                            // Fallback to ID token
                            if (idToken) {
                                try {
                                    const tokenPayload = JSON.parse(atob(idToken.split('.')[1]));
                                    actualEmail = tokenPayload.email ||
                                        tokenPayload.username ||
                                        tokenPayload['cognito:username'] ||
                                        tokenPayload['cognito:preferred_username'] ||
                                        actualEmail;
                                }
                                catch {
                                    // Keep the default email fallback.
                                }
                            }
                        }
                        // Get user profile from our API
                        const userProfile = await this.fetchCachedUserProfile(profileToken || '', actualEmail);
                        return userProfile;
                    }
                    catch (userError) {
                        console.error('authService: Could not get user details:', userError);
                        throw new Error('Failed to load user profile. Please try again.');
                    }
                }
                else if (session.tokens && this.isSessionExpired(session)) {
                    try {
                        // Try to refresh the session
                        const refreshedSession = await fetchAuthSession({ forceRefresh: true });
                        if (refreshedSession.tokens) {
                            // Recursive call with refreshed session
                            return await this.loadCurrentUser();
                        }
                    }
                    catch {
                        // Clear expired tokens
                        this.clearLocalAuthState();
                        return null;
                    }
                }
            }
            catch {
                // Fall through to local token recovery below.
            }
            // Fallback: Check if we have a valid token in localStorage from cross-domain auth
            // This handles the case where Amplify doesn't have a session but we received a valid token
            const storedToken = localStorage.getItem(this.TOKEN_KEY);
            if (storedToken) {
                try {
                    // Verify the token is not expired
                    const parts = storedToken.split('.');
                    if (parts.length === 3) {
                        const payload = JSON.parse(atob(parts[1]));
                        const expirationTime = payload.exp * 1000;
                        if (Date.now() < expirationTime) {
                            // Extract email from token
                            const email = payload.email ||
                                payload['cognito:username'] ||
                                payload.username ||
                                'authenticated@user.com';
                            // Try to fetch user profile using the stored token
                            const userProfile = await this.fetchCachedUserProfile(storedToken, email);
                            this.setImpersonationMode(this.isImpersonationToken(storedToken));
                            return userProfile;
                        }
                        else {
                            this.clearLocalAuthState();
                        }
                    }
                }
                catch {
                    this.clearLocalAuthState();
                }
            }
            // No valid session found
            return null;
        }
        catch (error) {
            console.error('authService: Get current user error:', error);
            return null;
        }
    }
    /**
     * Get stored authentication token
     */
    getToken() {
        if (typeof window === 'undefined')
            return null;
        const storedToken = this.getStoredTokenRecord();
        if (!storedToken)
            return null;
        if (storedToken.isExpired) {
            return null;
        }
        return storedToken.token;
    }
    async getActiveWorkspaceId() {
        const token = await this.getValidToken();
        const workspaceId = this.getWorkspaceIdFromPayload(this.getTokenPayload(token));
        if (workspaceId) {
            return workspaceId;
        }
        const user = await this.getCurrentUser();
        return user?.currentWorkspace?.workspaceId || null;
    }
    /**
     * Switch user's active workspace
     */
    async switchWorkspace(workspaceId) {
        try {
            const token = await this.getValidToken();
            const response = await fetch(`${this.API_BASE_URL}/user/workspace/switch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ workspaceId }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Failed to switch workspace');
            }
            if (this.isImpersonationToken(token)) {
                const nextToken = data.data?.impersonationToken;
                if (!nextToken) {
                    throw new Error('Missing updated impersonation token');
                }
                localStorage.setItem(this.TOKEN_KEY, nextToken);
                this.profileCache = null;
                this.setImpersonationMode(true);
                return {
                    success: true,
                    activeWorkspace: data.data?.activeWorkspace
                };
            }
            const workspaceSwitchToken = data.data?.workspaceSwitchToken;
            if (workspaceSwitchToken) {
                localStorage.setItem(this.TOKEN_KEY, workspaceSwitchToken);
                this.profileCache = null;
                this.setImpersonationMode(false);
                return {
                    success: true,
                    activeWorkspace: data.data?.activeWorkspace,
                };
            }
            // Refresh the Cognito session to get updated JWT with new custom attributes
            try {
                // First, let's try multiple refresh approaches
                const { fetchAuthSession } = await import('aws-amplify/auth');
                // Approach 1: Force refresh with cache clearing
                let refreshedSession = await fetchAuthSession({ forceRefresh: true });
                // Wait a bit for Cognito to propagate the custom attribute changes
                await new Promise(resolve => setTimeout(resolve, 2000));
                // Approach 2: Try another force refresh after waiting
                refreshedSession = await fetchAuthSession({ forceRefresh: true });
                if (refreshedSession.tokens?.idToken) {
                    const newToken = refreshedSession.tokens.idToken.toString();
                    const newPayload = JSON.parse(atob(newToken.split('.')[1]));
                    localStorage.setItem(this.TOKEN_KEY, newToken);
                    this.profileCache = null;
                    if (newPayload['custom:activeWorkspaceId'] !== workspaceId) {
                        console.warn('JWT still contains old workspace ID after workspace switch');
                    }
                }
                else {
                    console.warn('No ID token in refreshed session');
                }
            }
            catch (refreshError) {
                console.error('Failed to refresh Cognito session:', refreshError);
                // Don't fail the entire operation if token refresh fails
            }
            return {
                success: true,
                activeWorkspace: data.data?.activeWorkspace
            };
        }
        catch (error) {
            console.error('Error switching workspace:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }
    /**
     * Refresh authentication token
     */
    async refreshToken() {
        try {
            if (this.hasActiveImpersonationToken()) {
                throw new Error('Impersonation sessions cannot refresh via Cognito');
            }
            // Use Cognito's refresh token functionality
            const refreshedSession = await fetchAuthSession({ forceRefresh: true });
            if (refreshedSession.tokens?.accessToken) {
                const accessToken = refreshedSession.tokens.accessToken.toString();
                const idToken = refreshedSession.tokens.idToken?.toString();
                // Store the new tokens
                localStorage.setItem(this.TOKEN_KEY, idToken || accessToken);
                this.profileCache = null;
                this.setImpersonationMode(false);
                // Note: Refresh tokens are managed by Amplify internally
            }
            else {
                throw new Error('Failed to refresh tokens');
            }
        }
        catch (error) {
            console.error('Token refresh error:', error);
            if (this.hasActiveImpersonationToken() || this.isImpersonationModeActive()) {
                this.clearLocalAuthState();
            }
            else {
                await this.logout();
            }
            throw error;
        }
    }
    /**
     * Fetch user profile from API
     */
    async fetchCachedUserProfile(token, userEmail) {
        if (!token) {
            return this.fetchUserProfile(userEmail);
        }
        if (this.profileCache?.token === token &&
            Date.now() - this.profileCache.cachedAt < this.PROFILE_CACHE_TTL_MS) {
            return this.profileCache.user;
        }
        const user = await this.fetchUserProfile(userEmail);
        this.profileCache = { token, user, cachedAt: Date.now() };
        return user;
    }
    async fetchUserProfile(userEmail) {
        try {
            const token = this.getToken();
            if (!token) {
                throw new Error('No authentication token');
            }
            // Try to fetch from our API first
            try {
                const response = await fetch(`${this.API_BASE_URL}/user/profile`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data.success) {
                        // Transform API response to AuthUser format
                        const userProfile = {
                            userId: data.data.user.userId,
                            email: data.data.user.email,
                            name: data.data.user.name,
                            createdAt: data.data.user.createdAt,
                            workspaces: data.data.workspaces || [],
                            currentWorkspace: data.data.currentWorkspace,
                            impersonation: data.data.impersonation,
                        };
                        return userProfile;
                    }
                }
            }
            catch (apiError) {
                console.error('API not available:', apiError);
                throw new Error('Unable to fetch user profile from API');
            }
            // Fallback: Extract user data from the real Cognito token
            let email = userEmail || 'authenticated@user.com';
            let userId = '';
            let workspaceId = '';
            let name = '';
            let roles = ['viewer'];
            try {
                const tokenPayload = JSON.parse(atob(token.split('.')[1]));
                // Extract user information from real Cognito token
                email = tokenPayload.email ||
                    tokenPayload.username ||
                    tokenPayload['cognito:username'] ||
                    tokenPayload['cognito:preferred_username'] ||
                    email;
                userId = tokenPayload.sub || `user_${email.replace(/[@.]/g, '_')}`;
                name = tokenPayload.name ||
                    tokenPayload['cognito:name'] ||
                    tokenPayload.given_name ||
                    email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                // Extract workspace information from token claims
                workspaceId = tokenPayload.effectiveWorkspaceId ||
                    tokenPayload['custom:activeWorkspaceId'] ||
                    tokenPayload['custom:workspaceId'] ||
                    tokenPayload.workspaceId;
                // Extract roles
                if (tokenPayload['custom:roles']) {
                    try {
                        roles = JSON.parse(tokenPayload['custom:roles']);
                    }
                    catch (e) {
                        roles = [tokenPayload['custom:roles']];
                    }
                }
                else if (tokenPayload.roles) {
                    roles = Array.isArray(tokenPayload.roles) ? tokenPayload.roles : [tokenPayload.roles];
                }
                const activeWorkspaceRoleRaw = tokenPayload.effectiveWorkspaceRole ||
                    tokenPayload['custom:activeWorkspaceRole'] ||
                    tokenPayload['custom:workspaceRole'] ||
                    roles[0] ||
                    'viewer';
                const normalizedWorkspaceRole = String(activeWorkspaceRoleRaw).toLowerCase();
                const resolvedWorkspaceRole = normalizedWorkspaceRole === 'owner'
                    ? 'owner'
                    : normalizedWorkspaceRole === 'editor' ||
                        normalizedWorkspaceRole === 'admin' ||
                        normalizedWorkspaceRole === 'billing_manager' ||
                        normalizedWorkspaceRole === 'member'
                        ? 'editor'
                        : 'viewer';
                // If we have a workspace ID from the token, create the profile with real data
                if (workspaceId) {
                    return {
                        userId,
                        email,
                        name,
                        createdAt: new Date().toISOString(),
                        workspaces: [
                            {
                                workspaceId: workspaceId,
                                name: `${name}'s Workspace`,
                                role: resolvedWorkspaceRole,
                                joinedAt: new Date().toISOString(),
                            },
                        ],
                        currentWorkspace: {
                            workspaceId: workspaceId,
                            name: `${name}'s Workspace`,
                            role: resolvedWorkspaceRole,
                        },
                    };
                }
            }
            catch (tokenError) {
                console.error('fetchUserProfile: Could not parse Cognito token:', tokenError);
            }
            // No fallback - require proper API response
            throw new Error('Unable to extract user data from token');
        }
        catch (error) {
            console.error('Fetch user profile error:', error);
            throw new Error('Failed to fetch user profile from API');
        }
    }
    /**
     * Check if a session is expired
     */
    isSessionExpired(session) {
        try {
            if (!session.tokens?.accessToken)
                return true;
            const token = session.tokens.accessToken.toString();
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            const expirationTime = tokenPayload.exp * 1000; // Convert to milliseconds
            const currentTime = Date.now();
            return currentTime >= expirationTime;
        }
        catch (error) {
            console.error('Error checking session expiration:', error);
            return true; // Assume expired if we can't parse
        }
    }
    /**
     * Make authenticated API request
     */
    async apiRequest(endpoint, options = {}) {
        const token = await this.getValidToken();
        const response = await fetch(`${this.API_BASE_URL}${endpoint}`, {
            ...options,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers,
            },
        });
        if (response.status === 401) {
            // Token expired, try to refresh
            if (this.hasActiveImpersonationToken() || this.isImpersonationModeActive()) {
                this.clearLocalAuthState();
                throw new Error('Impersonation session expired. Please sign in again.');
            }
            await this.refreshToken();
            return this.apiRequest(endpoint, options);
        }
        if (!response.ok) {
            throw new Error(`API request failed: ${response.status}`);
        }
        return response.json();
    }
    /**
     * Migrate existing Cognito user to database
     */
    async migrateUser() {
        try {
            const response = await this.apiRequest('/user/migrate', {
                method: 'POST',
            });
            return response;
        }
        catch (error) {
            console.error('User migration error:', error);
            throw error;
        }
    }
    async requestPasswordReset(email) {
        try {
            await resetPassword({ username: email });
        }
        catch (error) {
            console.error('Request password reset error:', error);
            const errorMessage = this.getErrorMessage(error);
            throw new Error(errorMessage || 'Failed to request password reset');
        }
    }
    async confirmPasswordReset(email, confirmationCode, newPassword) {
        try {
            await confirmResetPassword({
                username: email,
                confirmationCode,
                newPassword,
            });
        }
        catch (error) {
            console.error('Confirm password reset error:', error);
            const errorMessage = this.getErrorMessage(error);
            throw new Error(errorMessage || 'Failed to reset password');
        }
    }
    /**
     * Cleanup duplicate workspaces for user
     */
    async cleanupDuplicateWorkspaces() {
        try {
            const response = await this.apiRequest('/user/cleanup-workspaces', {
                method: 'POST',
            });
            return response;
        }
        catch (error) {
            console.error('Workspace cleanup error:', error);
            throw error;
        }
    }
}
export const authService = new AuthService();
//# sourceMappingURL=authService.js.map